import React from 'react';
import { RocketLaunchIcon, UserGroupIcon, DocumentTextIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import { Link } from 'react-router-dom';

const TransparencyCharterStage3 = () => {
  return (
    <div className="space-y-8">
      <PlaybookSection 
        title="Stage 3: Engage, Embed & Reinforce" 
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />} 
        heading
      >
        <div className="bg-gray-800/30 p-6 rounded-lg border border-gray-700/50">
          <h3 className="text-xl font-semibold text-white mb-4">TL;DR</h3>
          
          <ExpandableCallout 
            type="info" 
            title="What do I need to DO in this stage?"
            defaultOpen={true}
          >
            <p className="text-gray-300 mb-4">You need to do 3 things:</p>
            <ol className="space-y-2">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">1</span>
                <span className="text-gray-300">Share your Transparency Charter with the wider team for feedback</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">2</span>
                <span className="text-gray-300">Identify ways you can embed & operationalise</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">3</span>
                <span className="text-gray-300">Establish an appropriate cadence to review</span>
              </li>
            </ol>
            <p className="text-gray-300 mt-4">Here's what we mean…</p>
          </ExpandableCallout>
        </div>
        
        <PlaybookCard className="mt-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <UserGroupIcon className="h-6 w-6 text-primary-light mr-3" />
            Embrace Co-Creation To Make Something That Sticks
          </h3>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              Whilst it's vital that your Exec team leads with drafting & documenting this transparency charter, embracing the opportunity to engage the wider business for feedback and input before it's finalised is a really powerful way of creating something that everyone feels they've had some involvement with. To steal a common phrase, "If they build it, they use it".
            </p>
            
            <p className="text-gray-300 mt-4">Here's how you could engage the team to solicit some feedback..</p>
            
            <ol className="mt-4 space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">1</span>
                <div>
                  <span className="text-gray-300">Share a notion doc / memo outlining your draft transparency charter</span>
                  <ul className="mt-2 pl-6 space-y-2">
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      <span className="text-gray-300">Try to include as much detail as you can, full draft if possible</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      <span className="text-gray-300">Make sure you give some context for the share too, and be clear on ask and outcomes. It will help them trust that their time will be well spent.</span>
                    </li>
                  </ul>
                </div>
              </li>
              
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">2</span>
                <div>
                  <span className="text-gray-300">Invite everyone to feedback, challenge and improve via comments over a 72 hour period</span>
                  <ul className="mt-2 pl-6 space-y-2">
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      <span className="text-gray-300">Here's an example comms template you could send out:</span>
                    </li>
                  </ul>
                </div>
              </li>
            </ol>
          </div>
          
          <CopyToNotion 
            title="Comms Template: Transparency Charter Feedback Request"
            description="A template for requesting feedback on a transparency charter"
            className="mt-6"
            tool="slack"
          >
            <div className="text-gray-300">
              <p><strong>Subject:</strong> Creating Our Transparency Charter</p>
              <p className="mt-4">Team,</p>
              <p className="mt-2">We're working on how we better define 'transparency' at [company].<br/>
              brief description of why;<br/>
              e.g. Because we lean into transparency in our values but are yet to define it<br/>
              e.g. Because we believe there's a need to align around a common definition<br/>
              e.g. Because we acknowledge there is a need to improve 'x' and believe this will help</p>
              
              <p className="mt-4"><strong>What we mean by a 'Transparency Charter'</strong><br/>
              A transparency charter lays out our definition of, and core approach to transparency<br/>
              at [company]. Through it we aim to offer clarity on what information we do share, don't<br/>
              share & why, and whether that information is shared internally or externally. It's used<br/>
              to help set & manage expectations for the entire team, and those external to the business<br/>
              too around what we communicate and share. We also want to use this to help us guide our<br/>
              decision making & comms day-to-day as a team.<br/>
              As an core outcome we hope this will get everyone aligned and on the same page with what<br/>
              'transparency' means here, which will help improve communication & trust.</p>
              
              <p className="mt-4"><strong>Our ask from you:</strong></p>
              <p>We believe in embracing some level of co-creation with this charter, and whilst we have<br/>
              drafted this as a leadership team, we would really value your individual input, feedback,<br/>
              and improvements to the charter, which you can access here.</p>
              <p className="mt-2">Please make efforts to feedback within 72 hours, so that we can push on with next iteration.</p>
              <p className="mt-4">Best,<br/>
              [Your Name]</p>
            </div>
          </CopyToNotion>
          
          <div className="prose prose-invert max-w-none mt-6">
            <ol className="space-y-4" start="3">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">3</span>
                <span className="text-gray-300">Assimilate the comments and share a v2 of the doc (repeat 2 and 3 as you need to)</span>
              </li>
              
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">4</span>
                <span className="text-gray-300">You're done!</span>
              </li>
            </ol>
          </div>
        </PlaybookCard>
        
        <PlaybookCard className="mt-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <DocumentTextIcon className="h-6 w-6 text-primary-light mr-3" />
            Embedding & Operationalising
          </h3>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              Once you've landed on a final version of your Charter it's time to get to work with embedding & operationalising it across the business. Here's some things you should consider:
            </p>
            
            <ul className="mt-4 space-y-6">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <p className="text-gray-300"><strong>Publish & Distribute</strong></p>
                  <ul className="mt-2 pl-6 space-y-2">
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      <span className="text-gray-300">On your company Handbook. Make sure it's accessible & available to all employees</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      <span className="text-gray-300">On your careers site & job ads, ensuring it's linked to any mention of core values (if relevant to transparency)</span>
                    </li>
                  </ul>
                </div>
              </li>
              
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <p className="text-gray-300"><strong>Establish Rituals</strong></p>
                  <ul className="mt-2 pl-6 space-y-2">
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      <span className="text-gray-300">Another potential co-creation opportunity, but ultimately a chance to establish a few ways in which you can continually reinforce this charter appropriately, contextual to the levels & areas of transparency you have opted to lean into in <Link to="/playbooks/transparency-charter/stage-2" className="text-primary hover:text-primary-light">Stage 2: Transparency Charter</Link></span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      <span className="text-gray-300">E.g. AMA Founder Forum's, Financial Updates at Townhalls, Slack emoji's to celebrate.</span>
                    </li>
                  </ul>
                </div>
              </li>
              
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <p className="text-gray-300"><strong>Consider any changes to 'Ways of work'</strong></p>
                  <ul className="mt-2 pl-6 space-y-2">
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      <span className="text-gray-300">Think about how you communicate day to day. Again, if appropriate to your context, do you need to default to a more public vs private mindset with DM's on Slack?</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      <span className="text-gray-300">Look at your Slack Analytics to understand a breakdown of current public vs private and set some goals!</span>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
        
            <div className="mt-6">
            <PlaybookImage 
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/transparency-charter/public-private-chart.png"
              alt="Example chart showing public vs private information categories"
              className="mt-6 rounded-lg border border-gray-700/50"
            />
            </div>
            
            <p className="text-gray-300 mt-4">
              Any changes you think about making to ways of work should of course be appropriate again to the unique definition you have identified for 'transparency' in <Link to="/playbooks/transparency-charter/stage-2" className="text-primary hover:text-primary-light">Stage 2: Transparency Charter</Link>.
            </p>
            
            <ul className="mt-6 space-y-6">
              <li className="flex items-start">
                <span className="text-primary-light mr-2">—</span>
                <div>
                  <p className="text-gray-300"><strong>Role-Modelling</strong></p>
                  <ul className="mt-2 pl-6 space-y-2">
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      <span className="text-gray-300">This is everyone's responsibility to uphold. It's important to impress that. And co-creation will play a huge part in ensuring that people do live & breathe it.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-primary-light mr-2">—</span>
                      <span className="text-gray-300">However, it is absolutely essential the Founders & Exec team can follow this and role-model this incredibly well. Any failure to do so will erode the trust that is generated over time.</span>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </div>
        </PlaybookCard>
        
        <PlaybookCard className="mt-8">
          <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
            <ArrowPathIcon className="h-6 w-6 text-primary-light mr-3" />
            Reviewing
          </h3>
          <div className="prose prose-invert max-w-none">
            <p className="text-gray-300">
              One crucial part of ensuring this Transparency Charter stands the test of time is to ensure that it evolves with your business. Know that it's OK to review, edit, amend this charter appropriately over time as your business evolves.
            </p>
            
            <div className="flex items-start mt-4">
              <span className="text-primary-light mr-2">—</span>
              <p className="text-gray-300">
                <strong>Example:</strong> It may be easy to share finances publicly as a startup, but if you reach IPO, public companies are often more limited about what they can share publicly day-to-day too. What is do-able now, might not be do-able in the future.
              </p>
            </div>
            
            <p className="text-gray-300 mt-4">It's important to do two things here:</p>
            
            <ol className="mt-4 space-y-4">
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">1</span>
                <span className="text-gray-300">Proactively review this charter at a time and cadence that feels appropriate to you. Once a year is probably a good practice. Again, embrace co-creation & employee feedback where possible</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-light font-bold mr-2">2</span>
                <span className="text-gray-300">Include a note on the Charter to make it clear to employees that this will be reviewed [every 'x' months] because of the reasons outlined above. This will help set & manage expectations if you do need to change anything in the future.</span>
              </li>
            </ol>
            
            <p className="text-gray-300 mt-6">Good luck!</p>
            
            <div className="mt-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
              <p className="text-gray-300">
                After completing this stage, proceed to <Link to="/playbooks/transparency-charter/useful-resources" className="text-primary hover:text-primary-light">Useful Resources</Link> for templates and additional materials to help with your transparency charter.
              </p>
            </div>
          </div>
        </PlaybookCard>
      </PlaybookSection>
    </div>
  );
};

export default TransparencyCharterStage3; 